import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TermsOfService = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to load site settings:', error);
        // Use defaults if fetch fails
        setSettings({
          company_name: "JAMOH & CO AUTOS LTD",
          office_address: "Plot 123, Victoria Island, Lagos, Nigeria",
          primary_email: "info@jamohautos.com",
          phone_number: "+234-800-JAMOH (52664)"
        });
      }
    };
    fetchSettings();
  }, []);

  if (!settings) {
    return null; // or loading skeleton
  }

  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center space-x-2 text-textSecondary hover:text-primary mb-6 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Home</span>
        </Link>

        <div className="glass p-8 rounded-sm">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-heading font-bold">Terms of Service</h1>
              <p className="text-textSecondary text-sm">Last updated: January 15, 2026</p>
            </div>
          </div>

          <div className="space-y-6 text-textSecondary">
            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">1. Acceptance of Terms</h2>
              <p className="leading-relaxed">
                By accessing and using {settings.company_name}'s online auction platform, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms of Service, please do not use our platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">2. Services Provided</h2>
              <p className="leading-relaxed mb-3">
                {settings.company_name} provides an online marketplace for buying and bidding on vehicles. Our services include:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Vehicle listings and auctions</li>
                <li>Bidding platform with real-time updates</li>
                <li>Payment processing services</li>
                <li>Vehicle sourcing from US auctions</li>
                <li>Import cost calculation tools</li>
                <li>Shipping and customs assistance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">3. User Accounts</h2>
              <p className="leading-relaxed mb-3">
                To participate in auctions, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Accept responsibility for all activities under your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">4. Bidding and Purchasing</h2>
              <p className="leading-relaxed mb-3">
                When placing a bid:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>All bids are legally binding commitments to purchase</li>
                <li>Winning bidders must complete payment within 48 hours</li>
                <li>Minimum bid increments are $100 USD</li>
                <li>Bids cannot be retracted once placed</li>
                <li>Reserve prices may apply to certain vehicles</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">5. Payment Terms</h2>
              <p className="leading-relaxed mb-3">
                Payment must be made through our approved methods:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Credit/debit cards via Stripe</li>
                <li>Bank transfer to our designated account</li>
                <li>All payments are in US Dollars (USD)</li>
                <li>A buyer's premium of 5% applies to all purchases</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">6. Shipping and Import</h2>
              <p className="leading-relaxed">
                {settings.company_name} assists with shipping and customs clearance to Nigeria. Buyers are responsible for all shipping costs, customs duties, and clearance fees. Import costs are estimates and may vary based on actual assessments.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">7. Vehicle Condition</h2>
              <p className="leading-relaxed">
                Vehicles are sold "as-is" with condition reports provided. We make reasonable efforts to accurately describe vehicle conditions, but buyers should inspect or arrange for inspection before bidding. We are not responsible for undisclosed defects.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">8. Dealer Registration</h2>
              <p className="leading-relaxed">
                Verified dealers can list vehicles on our platform. Dealers must provide valid business documentation and agree to our dealer terms. We reserve the right to verify and approve all dealer applications.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">9. Prohibited Activities</h2>
              <p className="leading-relaxed mb-3">
                Users may not:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Manipulate bidding or engage in shill bidding</li>
                <li>Misrepresent vehicle conditions or information</li>
                <li>Use the platform for fraudulent purposes</li>
                <li>Interfere with platform operations</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">10. Limitation of Liability</h2>
              <p className="leading-relaxed">
                JAMOH & CO AUTOS LTD is not liable for indirect, incidental, or consequential damages arising from platform use. Our total liability shall not exceed the purchase price of the vehicle in question.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">11. Governing Law</h2>
              <p className="leading-relaxed">
                These terms are governed by the laws of the Federal Republic of Nigeria. Any disputes shall be resolved in the courts of Lagos State, Nigeria.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">12. Changes to Terms</h2>
              <p className="leading-relaxed">
                We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Continued use of the platform constitutes acceptance of modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-white mb-3">13. Contact Information</h2>
              <p className="leading-relaxed">
                For questions about these terms, contact us at:
                <br /><br />
                <strong>{settings.company_name}</strong><br />
                {settings.office_address}<br />
                Email: {settings.primary_email}<br />
                Phone: {settings.phone_number}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;